import React from "react";
import PropTypes from "prop-types";
import { makeStyles, Typography } from "@material-ui/core";
import AnchorIcon from "@mui/icons-material/Anchor";
import StepBox from "../StepBox";
import PriceListBox from "../PriceListBox";

const useStyles = makeStyles(() => ({
  grid: {
    display: "grid",
    width: "100%",
    minHeight: "200px",
    gap: "12px 0px", // Remove column gaps: Row gap 12px, no column gap
    alignItems: "center", // Center items vertically in grid cells
    justifyItems: "stretch", // Stretch items horizontally to fill cells
  },
  trackLabel: {
    fontSize: "0.75rem",
    fontWeight: "bold",
    color: "#fff",
    textTransform: "capitalize",
    whiteSpace: "normal",
    wordBreak: "break-word",
    textOverflow: "wrap",
    overflowWrap: "anywhere", // More aggressive text wrapping
    hyphens: "auto", // Enable hyphenation
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingLeft: "8px",
    paddingRight: "4px",
    textAlign: "left",
    lineHeight: 1.2,
    zIndex: 15,
    textRendering: "optimizeLegibility",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    minWidth: 0, // Prevent overflow
    overflow: "hidden",
  },
}));

const ContentGrid = ({ config }) => {
  const classes = useStyles();
  const {
    gridTemplate,
    data,
    rowPositions,
    visual,
    isSkeletonMode = false,
    displayModes = ["price"], // Array of display modes for channels: 'margin', 'taxes', 'priceNet', 'priceGross'
    sellinDisplayModes = ["priceNet", "discount"], // Array of display modes for sellin: 'discount', 'taxes', 'priceNet', 'priceGross'
    showPriceList = true, // Toggle visibility of Price List column
    selectedAnchor = "",
    userImplementationColor,
  } = config;

  // Extract client color
  const clientColor =
    userImplementationColor || visual?.colors?.[0] || "#3498db";

  // Determine responsive grid template based on Price List visibility
  const getResponsiveGridTemplate = () => {
    if (showPriceList) {
      return gridTemplate.columns || "80px 1fr 140px 1fr 1fr 1fr";
    } else {
      // When Price List is hidden, remove first column
      return "1fr 140px 1fr 1fr 1fr";
    }
  };

  // Helper function to check if a step has actual data
  const checkStepHasData = (step) => {
    // Check for non-empty arrays
    if (
      Array.isArray(step.node2_reference_client) &&
      step.node2_reference_client.length > 0
    ) {
      return true;
    }
    if (
      Array.isArray(step.node3_reference_pos) &&
      step.node3_reference_pos.length > 0
    ) {
      return true;
    }

    // Check for direct step properties (excluding type and empty arrays)
    const stepProperties = Object.keys(step).filter(
      (key) =>
        key !== "type" &&
        key !== "node2_reference_client" &&
        key !== "node3_reference_pos",
    );

    return stepProperties.some((key) => {
      const value = step[key];
      return value !== undefined && value !== null && value !== "";
    });
  };

  const gridStyle = {
    gridTemplateRows: gridTemplate.rows,
    gridTemplateColumns: getResponsiveGridTemplate(),
  };

  // Adjust column positions when Price List is hidden
  const getAdjustedColumn = (originalColumn) => {
    return showPriceList ? originalColumn : originalColumn - 1;
  };

  // Create topology group labels above the first track of each group
  const createTopologyGroupLabels = () => {
    if (isSkeletonMode) {
      return [];
    }

    const labels = [];
    const { helpers } = config;

    // Use rowPositions to find the first track of each subsection
    const firstTrackRows = rowPositions.filter(
      (rowConfig) => rowConfig.isFirstInSubsection,
    );

    firstTrackRows.forEach((rowConfig) => {
      const subsection = data.topologySubsections[rowConfig.subsectionIndex];

      // Only create label if subsection has a name
      if (subsection.name && subsection.name.trim() !== "") {
        // Get the correct color for this subsection
        const subsectionColor =
          helpers?.getColorForSubsection?.(rowConfig.subsectionIndex) ||
          visual?.colors?.[rowConfig.subsectionIndex] ||
          "#666666";

        const labelStyle = {
          gridRow: rowConfig.row,
          gridColumn: getAdjustedColumn(3), // Track label column (adjusts when Price List hidden)
          color: subsectionColor,
          fontSize: "10px",
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingLeft: "8px",
          lineHeight: 1,
          zIndex: 5, // Below track labels
          pointerEvents: "none",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          transform: "translateY(-24px)", // Move up into the gap above the track
          position: "relative",
        };

        labels.push(
          <Typography
            key={`topology-group-${rowConfig.subsectionIndex}`}
            style={labelStyle}
            title={subsection.name}
          >
            {subsection.name}
          </Typography>,
        );
      }
    });

    return labels;
  };

  // Create track labels in the trackLabel column
  const createTrackLabels = () => {
    const labels = [];

    rowPositions.forEach((rowConfig) => {
      const subsection = data.topologySubsections[rowConfig.subsectionIndex];
      if (!subsection || !subsection.tracks) return;

      const track = subsection.tracks[rowConfig.trackIndex];
      if (!track || !track.label) return;

      // Format text for display - capitalize first letter of each word
      const formattedText = track.label
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");

      const labelStyle = {
        gridRow: rowConfig.row,
        gridColumn: getAdjustedColumn(3), // trackLabel column (adjusts when Price List hidden)
      };

      labels.push(
        <Typography
          key={`track-label-${track.id}`}
          className={classes.trackLabel}
          style={labelStyle}
        >
          {formattedText}
        </Typography>,
      );
    });
    return labels;
  };

  // Create step boxes for each track using new row configuration system
  // Helper to wrap step5 boxes with anchor icon if they're the selected anchor
  const wrapWithAnchorIcon = (stepBoxElement, trackId, stepType, gridStyle) => {
    // Only wrap step5 boxes that are the selected anchor
    if (stepType !== "step5" || trackId !== selectedAnchor || isSkeletonMode) {
      return stepBoxElement;
    }

    // Create a positioned wrapper that keeps stepbox centered and places icon to its right
    // The stepbox width is typically around 140px (70px center offset), icon is 12px away
    return (
      <div
        key={`anchor-wrapper-${trackId}`}
        style={{
          ...gridStyle,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {stepBoxElement}
        <AnchorIcon
          style={{
            position: "absolute",
            left: "50%",
            marginLeft: "70px", // Half stepbox width + 12px gap
            fontSize: "20px",
            color: clientColor,
            pointerEvents: "none",
          }}
        />
      </div>
    );
  };

  const createTrackContent = () => {
    const content = [];
    const positionMap = new Map(); // Track positions to detect conflicts

    // Use row configurations that include individual heights and stack information
    (rowPositions || config.rowPositions || []).forEach((rowConfig) => {
      const subsection = data.topologySubsections[rowConfig.subsectionIndex];
      const track = subsection.tracks[rowConfig.trackIndex];

      // Group steps by type and filter out empty steps
      const stepGroups = {};
      track.steps.forEach((step) => {
        // Check if step has actual data before including it
        const hasData = checkStepHasData(step);
        if (hasData) {
          if (!stepGroups[step.type]) {
            stepGroups[step.type] = [];
          }
          stepGroups[step.type].push(step);
        }
      });

      // Analyze available steps for centering logic
      const availableSteps = Object.keys(stepGroups);
      const hasStep2 = availableSteps.includes("step2");
      const hasStep3 = availableSteps.includes("step3");
      const hasStep4 = availableSteps.includes("step4");
      const hasStep5 = availableSteps.includes("step5");

      // Determine if we need to center steps (for Direct group or similar cases)
      const shouldCenterSteps = hasStep2 && !hasStep3 && (hasStep4 || hasStep5);

      // Process each step group
      Object.entries(stepGroups).forEach(([stepType, steps]) => {
        // Map step type to fixed column positions: priceList(1), step1(2), trackLabel(3), step2(4), step3(5), step5(6) - removed step4
        let stepColumn;
        let shouldSpanColumns = false;
        let columnSpan = 1;

        switch (stepType) {
          case "step1": // Always in Step1 column (Sellin) - adjusts when Price List hidden
            stepColumn = getAdjustedColumn(2);
            break;
          case "step2": // Step2 column, but center if step3 is missing
            if (shouldCenterSteps) {
              stepColumn = getAdjustedColumn(4);
              shouldSpanColumns = true;
              columnSpan = 2; // Span step2 and step3 columns (4/5)
            } else {
              stepColumn = getAdjustedColumn(4);
            }
            break;
          case "step3": // Always in Step3 column (node3_reference_pos)
            stepColumn = getAdjustedColumn(5);
            break;
          case "step4": // COMMENTED OUT - Margins column removed
            // stepColumn = 6;
            console.warn(
              `Step4 (Margins) has been removed from the visualization`,
            );
            return; // Skip step4 processing
          case "step5": // Always in Step5 column (moved from column 7 to 6)
            stepColumn = getAdjustedColumn(6);
            break;
          default:
            console.warn(`Unknown step type: ${stepType}`);
            return; // Skip unknown step types
        }

        if (steps.length === 1) {
          // Single step - use new row configuration system
          const step = steps[0];

          // Determine how many boxes this step needs
          const boxCount = Math.max(
            Array.isArray(step.node2_reference_client)
              ? step.node2_reference_client.length
              : 1,
            Array.isArray(step.node3_reference_pos)
              ? step.node3_reference_pos.length
              : 1,
            1, // Minimum 1 box
          );

          // Create a container div for stacked boxes if multiple boxes needed
          if (boxCount > 1) {
            const containerStyle = {
              gridRow: rowConfig.row,
              gridColumn: shouldSpanColumns
                ? `${stepColumn} / ${stepColumn + columnSpan}`
                : stepColumn,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              alignItems: "center",
              justifyContent: "center",
              minHeight: `${rowConfig.height}px`, // Use individual row height
            };

            const positionKey = `${rowConfig.row}-${stepColumn}`;

            // Check for position conflicts
            if (positionMap.has(positionKey)) {
              console.warn(
                `Position conflict at ${positionKey}! Previous: ${positionMap.get(positionKey)}, Current: ${track.id}-${step.type}`,
              );
            }
            positionMap.set(positionKey, `${track.id}-${step.type}-container`);

            // Create stacked boxes
            const stackedBoxes = [];
            for (let boxIndex = 0; boxIndex < boxCount; boxIndex++) {
              // Create step data for this specific box
              const boxStepData = { ...step };

              // If arrays exist, use the specific index data
              if (Array.isArray(step.node2_reference_client)) {
                Object.assign(
                  boxStepData,
                  step.node2_reference_client[boxIndex] || {},
                );
              }
              if (Array.isArray(step.node3_reference_pos)) {
                Object.assign(
                  boxStepData,
                  step.node3_reference_pos[boxIndex] || {},
                );
              }

              stackedBoxes.push(
                <StepBox
                  key={`step-${track.id}-${step.type}-${boxIndex}`}
                  step={boxStepData}
                  stepType={step.type}
                  trackId={track.id}
                  boxIndex={boxIndex}
                  isStacked={true}
                  isSkeletonMode={isSkeletonMode}
                  displayModes={
                    step.type === "step1" ? sellinDisplayModes : displayModes
                  }
                  selectedAnchor={selectedAnchor}
                  clientColor={clientColor}
                />,
              );
            }

            content.push(
              <div
                key={`step-container-${track.id}-${step.type}`}
                style={containerStyle}
              >
                {stackedBoxes}
              </div>,
            );
          } else {
            // Single box - use new row configuration
            const stepStyle = {
              gridRow: rowConfig.row,
              gridColumn: shouldSpanColumns
                ? `${stepColumn} / ${stepColumn + columnSpan}`
                : stepColumn,
              minHeight: `${Math.min(rowConfig.height * 0.8, 40)}px`, // 80% of row height, max 40px
              ...(shouldSpanColumns && {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }),
            };

            const positionKey = `${rowConfig.row}-${stepColumn}`;

            // Check for position conflicts
            if (positionMap.has(positionKey)) {
              console.warn(
                `Position conflict at ${positionKey}! Previous: ${positionMap.get(positionKey)}, Current: ${track.id}-${step.type}`,
              );
            }
            positionMap.set(positionKey, `${track.id}-${step.type}`);

            const stepBox = (
              <StepBox
                key={`step-${track.id}-${step.type}`}
                step={step}
                stepType={step.type}
                trackId={track.id}
                style={stepStyle}
                isSkeletonMode={isSkeletonMode}
                displayModes={
                  step.type === "step1" ? sellinDisplayModes : displayModes
                }
                selectedAnchor={selectedAnchor}
                clientColor={clientColor}
              />
            );

            // Wrap with anchor icon if this is step5 and selected anchor
            content.push(
              wrapWithAnchorIcon(stepBox, track.id, step.type, stepStyle),
            );
          }
        } else {
          // Multiple steps of the same type - stack them vertically with individual row height
          const containerStyle = {
            gridRow: rowConfig.row,
            gridColumn: shouldSpanColumns
              ? `${stepColumn} / ${stepColumn + columnSpan}`
              : stepColumn,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "center",
            justifyContent: "center",
            minHeight: `${rowConfig.height}px`, // Use individual row height
          };

          const positionKey = `${rowConfig.row}-${stepColumn}`;

          // Check for position conflicts
          if (positionMap.has(positionKey)) {
            console.warn(
              `Position conflict at ${positionKey}! Previous: ${positionMap.get(positionKey)}, Current: ${track.id}-${stepType}-multiple`,
            );
          }
          positionMap.set(positionKey, `${track.id}-${stepType}-multiple`);

          // Create stacked boxes for multiple steps of same type
          const stackedBoxes = steps.map((step, stepIndex) => (
            <StepBox
              key={`step-${track.id}-${step.type}-${stepIndex}`}
              step={step}
              stepType={step.type}
              trackId={track.id}
              boxIndex={stepIndex}
              isStacked={true}
              isSkeletonMode={isSkeletonMode}
              displayModes={
                step.type === "step1" ? sellinDisplayModes : displayModes
              }
              selectedAnchor={selectedAnchor}
              clientColor={clientColor}
            />
          ));

          content.push(
            <div
              key={`step-container-${track.id}-${stepType}`}
              style={containerStyle}
            >
              {stackedBoxes}
            </div>,
          );
        }
      });
    });

    return content;
  };

  // Create PriceListBox that spans all rows in column 1
  const createPriceListBox = () => {
    // Calculate total rows (all track rows)
    const totalRows = rowPositions.length;

    const priceListStyle = {
      gridRow: `1 / ${totalRows + 1}`, // Span all rows
      gridColumn: 1, // PriceList column
      display: "flex",
      alignItems: "stretch",
      justifyContent: "center",
      padding: "0 8px",
      height: "100%", // Fill the full height
    };

    return (
      <div key="price-list-container" style={priceListStyle}>
        <PriceListBox
          value={data.priceList}
          userColor={userImplementationColor}
          isSkeletonMode={isSkeletonMode}
          fullHeight={true}
        />
      </div>
    );
  };

  return (
    <div className={classes.grid} style={gridStyle}>
      {showPriceList && createPriceListBox()}
      {!isSkeletonMode && createTopologyGroupLabels()}
      {!isSkeletonMode && createTrackLabels()}
      {createTrackContent()}
    </div>
  );
};

ContentGrid.propTypes = {
  config: PropTypes.shape({
    rows: PropTypes.number.isRequired,
    columns: PropTypes.number.isRequired,
    gridTemplate: PropTypes.shape({
      rows: PropTypes.string.isRequired,
      columns: PropTypes.string.isRequired,
      responsive: PropTypes.shape({
        desktop: PropTypes.string,
        tablet: PropTypes.string,
        mobile: PropTypes.string,
      }),
    }).isRequired,
    rowPositions: PropTypes.arrayOf(
      PropTypes.shape({
        subsectionName: PropTypes.string.isRequired,
        subsectionIndex: PropTypes.number.isRequired,
        trackIndex: PropTypes.number.isRequired,
        row: PropTypes.number.isRequired,
        isFirstInSubsection: PropTypes.bool.isRequired,
        isLastInSubsection: PropTypes.bool.isRequired,
      }),
    ).isRequired,
    helpers: PropTypes.shape({
      getColorForSubsection: PropTypes.func,
    }),
    groupLabels: PropTypes.arrayOf(
      PropTypes.shape({
        subsectionName: PropTypes.string.isRequired,
        subsectionIndex: PropTypes.number.isRequired,
        row: PropTypes.number.isRequired,
        color: PropTypes.string.isRequired,
        trackId: PropTypes.string.isRequired,
      }),
    ),
    data: PropTypes.shape({
      priceList: PropTypes.number.isRequired,
      topologySubsections: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          tracks: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              label: PropTypes.string.isRequired,
              steps: PropTypes.arrayOf(PropTypes.object).isRequired,
            }),
          ).isRequired,
        }),
      ).isRequired,
    }).isRequired,
    visual: PropTypes.shape({
      colors: PropTypes.arrayOf(PropTypes.string),
    }),
    isSkeletonMode: PropTypes.bool,
    displayModes: PropTypes.arrayOf(PropTypes.string),
    sellinDisplayModes: PropTypes.arrayOf(PropTypes.string),
    showPriceList: PropTypes.bool,
    selectedAnchor: PropTypes.string,
    userImplementationColor: PropTypes.string,
  }).isRequired,
};

export default ContentGrid;
